import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  FileCode, 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  Loader2, 
  Sparkles, 
  LayoutGrid, 
  RefreshCw, 
  ArrowRight,
  Database,
  Eye,
  Info
} from 'lucide-react';
import { motion } from 'motion/react';

export interface CarouselBlueprint {
  id?: string;
  name: string;
  category: 'Educational' | 'Promotional' | 'Tension Challenge' | 'Technical Anatomy' | 'Case Study' | 'Generic';
  description: string;
  slideCount: number;
  narrative: string;
  active: boolean;
  createdAt?: string;
}

const DEFAULT_BLUEPRINTS: CarouselBlueprint[] = [
  {
    name: "The Hero's Journey (Case Study)",
    category: "Case Study",
    description: "Narrates a highly compelling, real transformation story or premium service result.",
    slideCount: 6,
    active: true,
    narrative: `Slide 1 (Hook): Scroll-stopping transformation headline. E.g. "How we helped [X] boost [Y] by [Z]% without [painpoint]". Massive bold key indicator.
Slide 2 (The Stuck State): Define the negative status quo, hidden struggles, and frustrating misconceptions.
Slide 3 (The Discovery Pivot): Introduce the breakthrough insight or customized strategy. What makes it unique.
Slide 4 (Actionable Method): Step-by-step visual infographic describing exact execution steps. Meticulous arrows or tags.
Slide 5 (The Proof/Win): Bold results showcase, comparative "Before vs After" or key metric blocks.
Slide 6 (CTA): Single, clear call-to-action inviting the user to DM or book a strategy session for similar outcomes.`
  },
  {
    name: "The Contrast Pivot (Authority Insight)",
    category: "Tension Challenge",
    description: "Challenges standard industry advice with deep expert commentary, replacing it with an authoritative guide.",
    slideCount: 5,
    active: true,
    narrative: `Slide 1 (Hook): Extreme tension hook. E.g., "Stop doing [standard advice] for [objective]." Or "Everything you've been taught about [topic] is wrong."
Slide 2 (The Lie/Trap): Explain why traditional advice creates a bottleneck or fails under pressure.
Slide 3 (The New Reality): Deep expert pivot: [Old Belief] -> [New Reality] -> [Consequence] -> [Action] formula.
Slide 4 (Core Rules): 3 highly clear, non-negotiable rules to navigate this new standard.
Slide 5 (CTA): Encourage comments/discussion around the insight. E.g., "Agree or disagree? Put your thoughts below."`
  },
  {
    name: "The Technical Anatomy (Breakdown)",
    category: "Technical Anatomy",
    description: "Breaks down a complex strategic, technical, or marketing system with precise anatomical pointers.",
    slideCount: 5,
    active: true,
    narrative: `Slide 1 (Hook): Detailed structural showcase. E.g. "The Anatomy of a High-Converting [Item/Process]".
Slide 2 (Foundation block): Pointing indicators demonstrating the starting elements of the anatomy.
Slide 3 (The Dynamic Connectors): Technical flow/arrows illustrating structural links between elements.
Slide 4 (The Power Trick): A hidden optimization hack that unlocks 10x value from this specific blueprint.
Slide 5 (CTA): Save checklist for reference later.`
  }
];

export const BlueprintEngine = ({ userEmail }: { userEmail: string }) => {
  const [blueprints, setBlueprints] = useState<CarouselBlueprint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<CarouselBlueprint['category']>('Educational');
  const [formDescription, setFormDescription] = useState('');
  const [formSlideCount, setFormSlideCount] = useState(5);
  const [formNarrative, setFormNarrative] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const fetchBlueprints = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = collection(db, 'carousel_blueprints');
      const snap = await getDocs(q);
      const list: CarouselBlueprint[] = [];
      snap.forEach(doc => {
        list.push({ id: doc.id, ...doc.data() } as CarouselBlueprint);
      });

      if (list.length === 0) {
        // Automatically seed with default blueprints for user experience
        const seeded: CarouselBlueprint[] = [];
        try {
          for (const item of DEFAULT_BLUEPRINTS) {
            const docRef = await addDoc(collection(db, 'carousel_blueprints'), {
              ...item,
              createdAt: new Date().toISOString()
            });
            seeded.push({ id: docRef.id, ...item });
          }
          setBlueprints(seeded);
        } catch (seedErr) {
          console.warn("Seeding database failed (likely due to permissions), falling back to local memory defaults:", seedErr);
          // Set in-memory defaults with pseudo IDs so the page works perfectly
          setBlueprints(DEFAULT_BLUEPRINTS.map((item, idx) => ({ id: `local-${idx}`, ...item })));
        }
      } else {
        setBlueprints(list);
      }
    } catch (err: any) {
      console.error("Error fetching blueprints:", err);
      setError(err.message || 'Failed to load blueprints.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlueprints();
  }, []);

  const handleToggleActive = async (item: CarouselBlueprint) => {
    if (!item.id) return;
    try {
      const docRef = doc(db, 'carousel_blueprints', item.id);
      await updateDoc(docRef, { active: !item.active });
      setBlueprints(prev => prev.map(b => b.id === item.id ? { ...b, active: !item.active } : b));
    } catch (err) {
      console.error("Error toggling active status:", err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'carousel_blueprints', id));
      setBlueprints(prev => prev.filter(b => b.id !== id));
      if (editId === id) {
        resetForm();
      }
    } catch (err) {
      console.error("Error deleting blueprint:", err);
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setFormName('');
    setFormCategory('Educational');
    setFormDescription('');
    setFormSlideCount(5);
    setFormNarrative('');
    setFormActive(true);
  };

  const handleStartEdit = (item: CarouselBlueprint) => {
    setIsEditing(true);
    setEditId(item.id || null);
    setFormName(item.name);
    setFormCategory(item.category);
    setFormDescription(item.description);
    setFormSlideCount(item.slideCount);
    setFormNarrative(item.narrative);
    setFormActive(item.active);
    
    // Scroll to the top of the page so the user actually sees the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStartAdd = () => {
    setIsEditing(true);
    setEditId(null);
    setFormName('');
    setFormCategory('Educational');
    setFormDescription('');
    setFormSlideCount(5);
    setFormNarrative(`Slide 1 (Hook): 
Slide 2 (Problem): 
Slide 3 (Value): 
Slide 4 (Summary): 
Slide 5 (CTA): `);
    setFormActive(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formNarrative.trim()) return;

    setIsSubmitting(true);
    try {
      const data: Omit<CarouselBlueprint, 'id'> = {
        name: formName,
        category: formCategory,
        description: formDescription,
        slideCount: Number(formSlideCount),
        narrative: formNarrative,
        active: formActive,
        createdAt: new Date().toISOString()
      };

      if (editId) {
        await updateDoc(doc(db, 'carousel_blueprints', editId), data);
        setBlueprints(prev => prev.map(b => b.id === editId ? { ...b, ...data } : b));
      } else {
        const docRef = await addDoc(collection(db, 'carousel_blueprints'), data);
        setBlueprints(prev => [...prev, { id: docRef.id, ...data }]);
      }
      resetForm();
    } catch (err) {
      console.error("Error saving blueprint:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-12">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="bg-indigo-600/10 p-2 rounded-xl text-indigo-600">
              <FileCode className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Carousel Blueprint Engine</h2>
          </div>
          <p className="text-gray-500 mt-1 sm:ml-11">
            Build and nurture a custom, secure knowledge base of structured layout blueprints to guide high-converting visual carousel generations. Only accessible to your email (<span className="font-semibold text-gray-700">{userEmail}</span>).
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={handleStartAdd}
            className="sm:ml-11 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl transition-all shadow-md shadow-indigo-100 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Add Blueprint</span>
          </button>
        )}
      </header>

      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200/80 rounded-3xl p-6 shadow-sm space-y-6"
        >
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {editId ? '✨ Edit Layout Blueprint' : '✨ Define New Layout Blueprint'}
            </h3>
            <button
              onClick={resetForm}
              className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-lg text-xs font-bold transition-all cursor-pointer"
            >
              Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-3 gap-5">
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block">Blueprint Name</label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. 5-Step Anatomy Transformation"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 text-sm text-gray-900"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block">Category</label>
                <select
                  value={formCategory}
                  onChange={e => setFormCategory(e.target.value as CarouselBlueprint['category'])}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 text-sm text-gray-900 bg-white"
                >
                  <option value="Educational">Educational</option>
                  <option value="Promotional">Promotional</option>
                  <option value="Tension Challenge">Tension Challenge</option>
                  <option value="Technical Anatomy">Technical Anatomy</option>
                  <option value="Case Study">Case Study</option>
                  <option value="Generic">Generic</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-5">
              <div className="space-y-1.5 md:col-span-3">
                <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block">Short Description</label>
                <input
                  type="text"
                  required
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                  placeholder="Summarize when the AI should trigger this layout blueprint..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 text-sm text-gray-900"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block">Target Slide Count</label>
                <input
                  type="number"
                  required
                  min={2}
                  max={10}
                  value={formSlideCount}
                  onChange={e => setFormSlideCount(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 text-sm text-gray-900 font-mono"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block">Narrative Flow Blueprint (Guidelines per Slide)</label>
                <span className="text-[10px] text-gray-400">Instruct the layout engine step-by-step</span>
              </div>
              <textarea
                required
                rows={10}
                value={formNarrative}
                onChange={e => setFormNarrative(e.target.value)}
                placeholder="Slide 1: Hook description...&#10;Slide 2: Context/Problem...&#10;Slide 3: Pivot..."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 font-mono text-xs leading-relaxed text-gray-900 bg-gray-50/50"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formActive}
                  onChange={e => setFormActive(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                />
                <span className="text-xs font-bold text-gray-700">Set Active immediately for AI execution</span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all shadow-md shadow-indigo-100 cursor-pointer flex items-center gap-1.5"
              >
                {isSubmitting && <Loader2 className="w-3 px-1 h-3 animate-spin" />}
                <span>{editId ? 'Save Blueprint' : 'Deploy Blueprint'}</span>
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-12 flex flex-col items-center justify-center gap-4 text-center">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          <p className="text-sm font-semibold text-gray-500">Retrieving security rules and layout blueprints...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-red-700 text-sm flex flex-col gap-2">
          <p className="font-bold">Error connecting to the Blueprint Engine:</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4.5 flex gap-3 text-emerald-800">
            <div className="bg-white p-1.5 h-fit rounded-lg text-emerald-600 shadow-sm">
              <Database className="w-4 h-4 animate-pulse" />
            </div>
            <div>
              <p className="text-xs font-bold">Knowledge Base Synced & Securing Client Isolation</p>
              <p className="text-[11px] opacity-90 mt-0.5 leading-relaxed">
                All blueprints reside in a designated master library. When users generate a carousel, the layout router automatically matches active templates to build matching narrative slides.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blueprints.map(b => (
              <div 
                key={b.id} 
                className={`bg-white border transition-all rounded-3xl p-5 flex flex-col justify-between shadow-sm hover:shadow-md ${b.active ? 'border-gray-200/80 hover:border-indigo-200' : 'border-gray-200/50 opacity-60'}`}
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50/70 px-2.5 py-1 rounded-full">
                      {b.category}
                    </span>
                    
                    <button
                      onClick={() => handleToggleActive(b)}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all cursor-pointer ${b.active ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-150 text-gray-500 border border-gray-200'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${b.active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      <span>{b.active ? 'Active' : 'Disabled'}</span>
                    </button>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 text-sm leading-snug">{b.name}</h4>
                    <p className="text-xs text-gray-500 mt-1 lines-2">{b.description}</p>
                  </div>

                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Target Carousel Size</span>
                    <span className="text-xs font-black text-indigo-700 font-mono bg-white border border-gray-200 px-2 py-0.5 rounded-md shadow-sm">
                      {b.slideCount} Slides
                    </span>
                  </div>

                  {activePreviewId === b.id ? (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="bg-indigo-50/30 border border-indigo-100/40 rounded-xl p-3.5 space-y-2 mt-4"
                    >
                      <div className="flex items-center gap-1.5 text-indigo-900 font-bold text-[10px] uppercase tracking-wider">
                        <Eye className="w-3.5 h-3.5" />
                        <span>AI Directives Preview</span>
                      </div>
                      <pre className="text-[10px] font-mono text-gray-600 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto bg-white p-2.5 rounded-lg border border-gray-100">
                        {b.narrative}
                      </pre>
                    </motion.div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-5">
                  <button
                    onClick={() => setActivePreviewId(activePreviewId === b.id ? null : b.id || null)}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-[10px] rounded-lg transition-all cursor-pointer"
                  >
                    <span>{activePreviewId === b.id ? "Hide Flow" : "Inspect Flow"}</span>
                  </button>

                  <div className="flex items-center gap-1.5">
                    {showDeleteConfirm === b.id ? (
                      <>
                        <button
                          onClick={() => handleDelete(b.id!)}
                          className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] rounded-lg transition-all cursor-pointer"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-[10px] rounded-lg transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleStartEdit(b)}
                          title="Edit Blueprint"
                          className="p-2 text-gray-500 bg-gray-50 hover:bg-gray-150 hover:text-indigo-600 rounded-lg transition-all cursor-pointer"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(b.id!)}
                          title="Delete Blueprint"
                          className="p-2 text-gray-400 bg-gray-50 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-indigo-950 text-white rounded-3xl p-6 relative overflow-hidden shadow-lg shadow-indigo-950/20">
            <div className="absolute top-0 right-0 transform translate-x-12 -translate-y-12 w-64 h-64 bg-indigo-800/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 transform -translate-x-12 translate-y-12 w-64 h-64 bg-indigo-800/10 rounded-full blur-2xl"></div>

            <div className="relative space-y-4 max-w-2xl">
              <div className="flex items-center gap-2 text-indigo-400">
                <Sparkles className="w-4 h-4 animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-wider">Internal Routing System</span>
              </div>
              <h4 className="text-xl font-bold tracking-tight">How the AI accesses client-isolated blueprints</h4>
              <p className="text-xs text-indigo-200/90 leading-relaxed">
                When generating a social media calendar or a carousel draft, the generator query dynamically executes a secure read block on active documents within your account's collection. The matching layout instructions are seamlessly appended to the system rules to ensure the creative pipeline results in compliant, structurally sound slides.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
